/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/**
 * @Author : Nishant Singh
 */

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, ScrollView } from 'react-native';
import { buttonFloating } from '@/assets/styles/buttonStyles';
import SociollaText from '@components/sociolla/SociollaText';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import SelectBox from '@components/form/SelectBox';
import * as Layout from '@components/screen/Layout';
import { isWeb } from '@global/screens';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';

import useApi from '@/hooks/useApi';
import { MsAccount } from '@api/ms-account';

const CustomerClusterFormScreen = (props) => {
  const screenName = 'Customer Cluster';
  const dispatch = useDispatch();
  const [detailData, setDetailData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const getCustomerClusterRuleDetail = useApi(
    MsAccount.getCustomerClusterRuleDetail,
  );

  const initialValue = {
    name: isEdit && detailData?.name ? detailData?.name : '',
    description:
      isEdit && detailData?.description ? detailData?.description : '',
    is_active: isEdit && detailData?.is_active ? detailData?.is_active : false,
    from_purchased_range:
      isEdit && detailData?.from_purchased_range
        ? detailData?.from_purchased_range
        : '',
    to_purchased_range:
      isEdit && detailData?.to_purchased_range
        ? detailData?.to_purchased_range
        : '',
  };

  const FormValidationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Name is required.')
      .test(
        'alphabets',
        'Name must contain alphabets',
        (value) => /[^0-9]/.test(value) && /[a-zA-Z]/.test(value),
      ),
    description: Yup.string().required('Description is required.'),
    is_active: Yup.boolean().required('Status is required.'),
    from_purchased_range: Yup.number()
      .positive('From purchased range must be a positive number.')
      .integer('From purchased range must be a whole number.')
      .required('From purchased range is required.')
      .label('From purchased range')
      .typeError('From Purchased range must be number!'),
    to_purchased_range: Yup.number()
      .positive('To purchased range must be a positive number.')
      .integer('To purchased range must be a whole number.')
      .required('To purchased range is required.')
      .label('To purchased range')
      .moreThan(
        Yup.ref('from_purchased_range'),
        'To purchased range must be greater than from purchased range.',
      )
      .typeError('To Purchased range must be number!'),
  });

  // ...............hooks
  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setDetailData({});
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  // .........methods
  const getDetail = async (id) => {
    const response = await getCustomerClusterRuleDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      setDetailData(response.data.data);
    }
  };

  const messageHandler = (type = 'success', errorMsg) => {
    dispatch(showPopup({ name: 'isLoading', value: false }));
    const message = {
      success: {
        text1: 'New Customer Cluster successfully created',
        type: 'success',
      },
      error: { text1: errorMsg ?? 'A system error occurred', type: 'error' },
      update: {
        text1: 'Customer Cluster changed successfully',
        type: 'success',
      },
    };
    Toast.show({ ...message[type] });
  };

  const submitHandler = async (payload) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      if (isEdit) {
        const updateData = await MsAccount.updateCustomerClusterRule(
          detailData._id,
          payload,
        );
        if (updateData?.data?.data) {
          // messageHandler('update')
          dispatch(showPopup({ name: 'isLoading', value: false }));
          dispatch(
            showAlert({
              show: true,
              title: 'Changes Saved',
              description: 'Customer cluster changed successfully.',
              isConfirm: false,
              onCallback: () => {
                props.navigation.navigate('CustomerClusterDetailScreen', {
                  titleScreen: detailData.name,
                  name: detailData.name,
                  id: detailData._id,
                });
              },
            }),
          );
        } else {
          messageHandler('error');
        }
      } else {
        const createData = await MsAccount.createCustomerClusterRule(payload);
        if (createData?.data?.data) {
          const result = createData.data.data;
          // messageHandler()
          dispatch(showPopup({ name: 'isLoading', value: false }));
          dispatch(
            showAlert({
              show: true,
              title: 'Customer Cluster Created',
              description: 'New customer cluster successfully created.',
              isConfirm: false,
              onCallback: () => {
                if (result._id) {
                  props.navigation.navigate('CustomerClusterDetailScreen', {
                    titleScreen: result.name,
                    name: result.name,
                    id: result._id,
                  });
                } else {
                  props.navigation.navigate('CustomerClusterScreen');
                }
              },
            }),
          );
        } else {
          messageHandler('error');
        }
      }
    } catch (error) {
      messageHandler('error', error?.response?.data?.message);
    }
  };

  return (
    <Layout.Container>
      {isEdit && getCustomerClusterRuleDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          initialValues={initialValue}
          validationSchema={FormValidationSchema}
          onSubmit={(payload) => submitHandler(payload)}>
          {({
            handleChange,
            setFieldValue,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back
                    title={screenName}
                    onCallback={() =>
                      isEdit
                        ? props.navigation.navigate(
                            'CustomerClusterDetailScreen',
                            props.route.params,
                          )
                        : props.navigation.navigate('CustomerClusterScreen')
                    }
                  />
                  <Layout.Title
                    title={`${isEdit ? 'Edit' : 'Add'} ${screenName}`}>
                    <Button
                      rounded={true}
                      buttonStyle={{ width: 120 }}
                      title={
                        <SociollaText
                          type="bold"
                          style={{
                            fontSize: 14,
                            letterSpacing: 0.5,
                            color: Colors.white,
                          }}>
                          {isEdit ? 'Update Cluster' : 'Create Cluster'}
                        </SociollaText>
                      }
                      onPress={handleSubmit}
                    />
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded isHeight>
                <ScrollView>
                  <Layout.FormWrapper
                    style={{
                      width: isWeb ? 400 : '100%',
                      paddingHorizontal: 15,
                      ...(isWeb
                        ? {
                            paddingHorizontal: 20,
                            paddingTop: 20,
                            marginBottom: 15,
                          }
                        : {}),
                    }}>
                    <Layout.FormGroup>
                      <Input
                        label="Cluster Name"
                        value={values.name}
                        isRequired={true}
                        placeholder="Name"
                        hasError={
                          errors.name && touched.name ? errors.name : null
                        }
                        onChangeText={handleChange('name')}
                      />
                    </Layout.FormGroup>
                    <Layout.FormGroup>
                      <Input
                        label="Cluster Description"
                        value={values.description}
                        isRequired={true}
                        placeholder="Description"
                        hasError={
                          errors.description && touched.description
                            ? errors.description
                            : null
                        }
                        onChangeText={handleChange('description')}
                      />
                    </Layout.FormGroup>

                    <Layout.FormGroup style={{ zIndex: 1000 }}>
                      <SelectBox
                        label="Status"
                        isDropdown={true}
                        options={[
                          { name: 'Active', value: true },
                          { name: 'Inactive', value: false },
                        ]}
                        value={
                          values.is_active
                            ? { name: 'Active', value: true }
                            : { name: 'In Active', value: false }
                        }
                        placeholder="Select Status"
                        trackBy="value"
                        labelKey="name"
                        isRequired={true}
                        hasError={
                          errors.is_active && touched.is_active
                            ? errors.is_active
                            : null
                        }
                        onChange={(value) =>
                          setFieldValue('is_active', value.value)
                        }
                      />
                    </Layout.FormGroup>

                    <>
                      <View style={{ flexDirection: 'row', flexShrink: 1 }}>
                        <Layout.FormGroup style={{ flex: 1 }}>
                          <Input
                            label={
                              <View style={{ flexDirection: 'column' }}>
                                <SociollaText>Purchased Range </SociollaText>
                                <SociollaText>From</SociollaText>
                              </View>
                            }
                            labelStyle={{ fontSize: 8 }}
                            type="number"
                            value={values.from_purchased_range}
                            isRequired={true}
                            placeholder="From"
                            hasError={
                              errors.from_purchased_range &&
                              touched.from_purchased_range
                                ? errors.from_purchased_range
                                : null
                            }
                            onChangeText={handleChange('from_purchased_range')}
                          />
                        </Layout.FormGroup>

                        <Layout.FormGroup style={{ flex: 1, alignSelf: 'end' }}>
                          <Input
                            label="To"
                            value={values.to_purchased_range}
                            isRequired={true}
                            style={{ alignSelf: 'baseline' }}
                            placeholder="To"
                            hasError={
                              errors.to_purchased_range &&
                              touched.to_purchased_range
                                ? errors.to_purchased_range
                                : null
                            }
                            onChangeText={handleChange('to_purchased_range')}
                          />
                        </Layout.FormGroup>
                      </View>
                    </>
                  </Layout.FormWrapper>
                </ScrollView>
              </Layout.Wrapper>
              {!isWeb ? (
                <View style={buttonFloating}>
                  <Button
                    rounded={true}
                    buttonStyle={{ width: '100%' }}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        {isEdit ? `Update Cluster` : `Create Cluster`}
                      </SociollaText>
                    }
                    onPress={handleSubmit}
                  />
                </View>
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

// const styles = StyleSheet.create({
// 	wrapper: {
// 		backgroundColor: Colors.white,
// 		position: 'relative'
// 	},
// 	labelText: {
// 		fontSize: 13
// 	}
// })

export default CustomerClusterFormScreen;
